// Operator presence (api-phase2.md "Presence"): the pill in the header, and the state the console reads
// (my groups for "Sent to me", my cap). Available / Busy / Break are set with PUT /api/presence/me;
// Offline is shown whenever the realtime stream is down (the router stops offering after 60 s without it).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, errorText, SETTABLE_PRESENCE, type Presence, type PresenceStatus, type SettablePresence } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { useNow } from '../hooks.ts';
import { useSession } from '../session.tsx';
import { useStreamEvent, useStreamStatus } from '../stream.tsx';
import { dur } from '../console/util.ts';
import { setMuted, useMuted } from './sound.ts';

export interface PresenceState {
  /** Server presence (null until loaded, or on a server without Phase 2). */
  me: Presence | null;
  /** What to show: offline while the stream is down. */
  shown: PresenceStatus | null;
  /** The server has no /api/presence (Phase 1). */
  unsupported: boolean;
  error: unknown;
  busy: boolean;
  set: (s: SettablePresence) => Promise<void>;
  reload: () => void;
}

const Ctx = createContext<PresenceState | null>(null);

export function usePresence(): PresenceState | null {
  return useContext(Ctx);
}

export const PRESENCE_LABEL: Record<PresenceStatus, string> = { available: 'Available', busy: 'Busy', break: 'Break', offline: 'Offline' };

export function PresenceProvider({ children }: { children: ReactNode }) {
  const s = useSession();
  const enabled = s.has('operator', 'supervisor', 'admin');
  const stream = useStreamStatus();
  const [me, setMe] = useState<Presence | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [unsupported, setUnsupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const reload = useCallback(() => {
    if (!enabled) return;
    const n = ++seq.current;
    api.presenceMe().then(
      (p) => {
        if (n !== seq.current) return;
        setMe(p);
        setError(null);
        setUnsupported(false);
      },
      (e: unknown) => {
        if (n !== seq.current) return;
        if (e instanceof ApiError && (e.status === 404 || e.status === 405)) setUnsupported(true);
        else setError(e);
      },
    );
  }, [enabled]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30_000);
    return () => clearInterval(t);
  }, [reload]);

  // After the stream reconnects the server sees me online again: refetch.
  const wasOpen = useRef(stream.state === 'open');
  useEffect(() => {
    const open = stream.state === 'open';
    if (open && !wasOpen.current) reload();
    wasOpen.current = open;
  }, [stream.state, reload]);

  // Two expired offers in a row make me busy on the server; my counts change with offers and raises.
  useStreamEvent('offer_withdrawn', () => setTimeout(reload, 300));
  useStreamEvent('raise', () => setTimeout(reload, 300));

  const set = useCallback(async (status: SettablePresence) => {
    setBusy(true);
    setError(null);
    try {
      setMe(await api.setPresence(status));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, []);

  const shown: PresenceStatus | null = !me ? null : stream.state !== 'open' ? 'offline' : me.status;
  const value = useMemo<PresenceState | null>(
    () => (enabled ? { me, shown, unsupported, error, busy, set, reload } : null),
    [enabled, me, shown, unsupported, error, busy, set, reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The header pill: status, load against the cap, and a menu to change status and mute sounds. */
export function PresencePill() {
  const p = usePresence();
  const muted = useMuted();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const now = useNow(15_000);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc, true);
    };
  }, [open]);
  if (!p || p.unsupported) return null;
  const st = p.shown;
  const me = p.me;
  const since = me ? (me.since ? Date.parse(me.since) : NaN) : NaN;
  const title = !me
    ? p.error
      ? `Presence unavailable: ${errorText(p.error)}`
      : 'Loading presence…'
    : st === 'offline'
      ? 'Offline: the realtime stream is down, so no events are offered to you. It reconnects by itself.'
      : `${PRESENCE_LABEL[me.status]}${Number.isFinite(since) ? ` for ${dur(now - since)}` : ''} · ${me.activeCount}/${me.cap} active · ${me.parkedCount} parked${me.routingPaused ? ' · routing paused by a supervisor' : ''}`;
  return (
    <div className="pres" ref={ref}>
      <button
        type="button"
        className={`status pr-${st ?? 'loading'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
        onClick={() => setOpen((o) => !o)}
        disabled={!me && !p.error}
      >
        <i aria-hidden="true" />
        {st ? PRESENCE_LABEL[st] : p.error ? 'Presence ?' : '…'}
        {me && st !== 'offline' && (
          <span className="pres-load mono">
            {me.activeCount}/{me.cap}
          </span>
        )}
        {me?.routingPaused && <span className="pres-paused">paused</span>}
        <Icon name="down" />
      </button>
      {open && (
        <div className="pres-menu" role="menu" aria-label="Set your status">
          {SETTABLE_PRESENCE.map((k) => (
            <button
              key={k}
              type="button"
              role="menuitemradio"
              aria-checked={me?.status === k}
              className={`pres-opt pr-${k}${me?.status === k ? ' on' : ''}`}
              disabled={p.busy}
              onClick={() => {
                void p.set(k);
                setOpen(false);
              }}
            >
              <i aria-hidden="true" />
              <span>
                {PRESENCE_LABEL[k]}
                <small>{k === 'available' ? 'Offer me events' : k === 'busy' ? 'No new offers; keep my events' : 'Away; no new offers'}</small>
              </span>
            </button>
          ))}
          <div className="pres-sep" />
          <button type="button" role="menuitemcheckbox" aria-checked={muted} className="pres-opt" onClick={() => setMuted(!muted)}>
            <Icon name="speaker" />
            <span>
              {muted ? 'Sounds muted' : 'Sounds on'}
              <small>Offer and raise chimes</small>
            </span>
          </button>
          {me?.routingPaused && <div className="note warn-t pres-note">A supervisor paused routing to you. You keep your events.</div>}
          {st === 'offline' && <div className="note bad-t pres-note">Stream down: you show as offline until it reconnects.</div>}
          {p.error ? <div className="note bad-t pres-note">{errorText(p.error)}</div> : null}
        </div>
      )}
    </div>
  );
}

/** A small mute toggle (offer card and console). */
export function MuteToggle({ className = '' }: { className?: string }) {
  const muted = useMuted();
  return (
    <button
      type="button"
      className={`btn icon ${className}${muted ? ' muted' : ''}`}
      onClick={() => setMuted(!muted)}
      aria-pressed={muted}
      aria-label={muted ? 'Unmute sounds' : 'Mute sounds'}
      title={muted ? 'Sounds muted (click to unmute)' : 'Mute offer and raise sounds'}
    >
      <Icon name={muted ? 'mute' : 'speaker'} />
    </button>
  );
}
